import { PermitApplicationStep } from "./PermitApplicationContext";

/**
 * Configuration for each step in the permit application flow
 */
export interface StepConfig {
  readonly step: PermitApplicationStep;
  readonly label: string;
  readonly path: string;
}

/**
 * Ordered list of permit application steps with their labels and URL paths
 */
export const PERMIT_APPLICATION_STEPS: readonly StepConfig[] = [
  { step: PermitApplicationStep.SelectAuthority, label: "Select Authority", path: "authority" },
  { step: PermitApplicationStep.SelectPermitType, label: "Select Permit Type", path: "permit-type" },
  { step: PermitApplicationStep.ProvideInformation, label: "Provide Information", path: "information" },
  { step: PermitApplicationStep.UploadDocuments, label: "Upload Documents", path: "documents" },
  { step: PermitApplicationStep.ReviewConfirm, label: "Review & Confirm", path: "review" },
] as const;

/**
 * Base path for permit application routes
 */
export const PERMIT_APPLICATION_BASE_PATH = "/permits/apply";

/**
 * Get the step enum value from a URL path segment
 * @param path - The path segment (e.g., "authority", "permit-type")
 * @returns The corresponding PermitApplicationStep or null if not found
 */
export function getStepFromPath(path: string): PermitApplicationStep | null {
  // Extract the last segment if a full path is provided
  const segment = path.split("/").pop();
  const config = PERMIT_APPLICATION_STEPS.find(
    (s) => s.path === (segment === "apply" || segment === "" ? "authority" : segment),
  );
  return config?.step ?? null;
}

/**
 * Get the URL path segment for a given step
 * @param step - The PermitApplicationStep enum value
 * @returns The corresponding path segment or empty string if not found
 */
export function getPathFromStep(step: PermitApplicationStep): string {
  const config = PERMIT_APPLICATION_STEPS.find((s) => s.step === step);
  return config?.path ?? "";
}

/**
 * Get the full URL path for a given step
 * @param step - The PermitApplicationStep enum value
 * @returns The full path (e.g., "/permits/apply/authority")
 */
export function getFullPathFromStep(step: PermitApplicationStep): string {
  const path = getPathFromStep(step);
  return path ? `${PERMIT_APPLICATION_BASE_PATH}/${path}` : PERMIT_APPLICATION_BASE_PATH;
}

/**
 * Get the step label for display
 * @param step - The PermitApplicationStep enum value
 * @returns The human-readable label or empty string if not found
 */
export function getStepLabel(step: PermitApplicationStep): string {
  const config = PERMIT_APPLICATION_STEPS.find((s) => s.step === step);
  return config?.label ?? "";
}

/**
 * Get the step index (0-based) for a given step
 * @param step - The PermitApplicationStep enum value
 * @returns The 0-based index or -1 if not found
 */
export function getStepIndex(step: PermitApplicationStep): number {
  return PERMIT_APPLICATION_STEPS.findIndex((s) => s.step === step);
}

/**
 * Get the next step after the given step
 * @param step - The current PermitApplicationStep
 * @returns The next step or null if at the last step
 */
export function getNextStep(step: PermitApplicationStep): PermitApplicationStep | null {
  const index = getStepIndex(step);
  if (index === -1 || index >= PERMIT_APPLICATION_STEPS.length - 1) {
    return null;
  }
  return PERMIT_APPLICATION_STEPS[index + 1].step;
}

/**
 * Get the previous step before the given step
 * @param step - The current PermitApplicationStep
 * @returns The previous step or null if at the first step
 */
export function getPreviousStep(step: PermitApplicationStep): PermitApplicationStep | null {
  const index = getStepIndex(step);
  if (index <= 0) {
    return null;
  }
  return PERMIT_APPLICATION_STEPS[index - 1].step;
}

/**
 * Total number of steps in the permit application flow
 */
export const TOTAL_STEPS = PERMIT_APPLICATION_STEPS.length;
